import { type ExpressionBuilder, type Transaction } from "@rocicorp/zero";
import { defineQuery } from "../../zero/transformer";
import { zql, type Schema } from "../../zero/schema";
import { AccessGrant } from "@corpuscomplete/core/auth/grant";
import { z } from "zod/v4";
import { withZeroTransaction } from "../../zero/utils";
import type { UserActor } from "@corpuscomplete/core/actor";
import { accessDenied } from "@corpuscomplete/core/utils";

/**
 * List-collection namespace boundary for root/contents grant enforcement.
 *
 * Invariants:
 * - Root permissions control collection-level metadata actions.
 * - Contents permissions control list creation/editing within a collection.
 *
 * @see `packages/core/docs/namespace.md`
 */
export namespace ListCollection {
  const MAX_NAME_LENGTH = 100;
  export const forContents = defineQuery(
    z.object({ limit: z.number().min(1).max(50).optional() }),
    ({ ctx, args: { limit = 10 } }) => {
      return zql.listCollection.where((e) => viewableContents(e, ctx.actor)).limit(limit);
    },
  );

  export const Permissions = AccessGrant.definePermissions({
    canRename: AccessGrant.criteria("list_collection", "root", ["owner", "editor"]),
    canDelete: AccessGrant.criteria("list_collection", "root", ["owner"]),
    canAddContent: AccessGrant.criteria("list_collection", "contents", ["editor"]),
  });

  function viewableContents(
    { exists }: ExpressionBuilder<"listCollection", Schema>,
    actor: UserActor,
  ) {
    return exists("grants", (r) =>
      AccessGrant.matching(
        r,
        actor,
        AccessGrant.criteria("list_collection", "contents", ["editor", "viewer"]),
      ),
    );
  }

  export const forContentManagement = defineQuery(
    z.object({ limit: z.number().min(1).max(50).optional() }),
    ({ ctx, args: { limit = 10 } }) => {
      return zql.listCollection.where((e) => hasContentsEditorGrant(e, ctx.actor)).limit(limit);
    },
  );

  function hasContentsEditorGrant(
    { exists }: ExpressionBuilder<"listCollection", Schema>,
    actor: UserActor,
  ) {
    return exists("grants", (r) =>
      AccessGrant.matching(
        r,
        actor,
        AccessGrant.criteria("list_collection", "contents", ["editor"]),
      ),
    );
  }

  export const forRootManagement = defineQuery(
    z.object({ limit: z.number().min(1).max(50).optional() }),
    ({ ctx, args: { limit = 10 } }) => {
      return zql.listCollection
        .where((e) => viewableRoot(e, ctx.actor))
        .related("grants", (q) =>
          AccessGrant.matching(
            q,
            ctx.actor,
            AccessGrant.criteria("list_collection", "root", ["owner", "editor", "viewer"]),
          ),
        )
        .related("lists")
        .limit(limit);
    },
  );

  function viewableRoot({ exists }: ExpressionBuilder<"listCollection", Schema>, actor: UserActor) {
    return exists("grants", (r) =>
      AccessGrant.matching(
        r,
        actor,
        AccessGrant.criteria("list_collection", "root", ["owner", "editor", "viewer"]),
      ),
    );
  }

  export const myDefault = defineQuery(({ ctx }) => {
    return zql.listCollection.where("defaultForUserId", "=", ctx.actor.userId).one();
  });

  export type CreateMyDefault = z.infer<typeof createMyDefault.schema>;
  export const createMyDefault = withZeroTransaction(
    z.object({ id: z.uuid() }),
    async ({ tx, ctx, args: { id } }) => {
      const existing = await tx.run(
        ListCollection.myDefault.fn({
          ctx,
          args: undefined,
        }),
      );
      if (existing) {
        throw new Error("Default collection already exists for user");
      }
      await tx.mutate.listCollection.insert({
        id,
        name: "My Lists",
        defaultForUserId: ctx.actor.userId,
      });
      await Promise.all([
        // TODO: Consider not giving the owner role to the user here?
        // Would imply that the user can't change roles, which might already be a control I add to default for user collections
        AccessGrant.add(tx, AccessGrant.assignment(id, "list_collection", "root", "owner"), {
          type: "user",
          id: ctx.actor.userId,
        }),
        AccessGrant.add(tx, AccessGrant.assignment(id, "list_collection", "contents", "editor"), {
          type: "user",
          id: ctx.actor.userId,
        }),
      ]);
    },
  );

  export type Create = z.infer<typeof create.schema>;
  export const create = withZeroTransaction(
    z.object({
      id: z.uuid(),
      name: z.string().min(1).max(MAX_NAME_LENGTH),
    }),
    async ({ tx, ctx, args: { id, name } }) => {
      await tx.mutate.listCollection.insert({
        id,
        name: name.trim(),
        defaultForUserId: null,
      });
      await Promise.all([
        AccessGrant.add(tx, AccessGrant.assignment(id, "list_collection", "root", "owner"), {
          type: "user",
          id: ctx.actor.userId,
        }),
        AccessGrant.add(tx, AccessGrant.assignment(id, "list_collection", "contents", "editor"), {
          type: "user",
          id: ctx.actor.userId,
        }),
      ]);
    },
  );

  async function hasRootGrant(
    tx: Transaction,
    actor: UserActor,
    collectionId: string,
    roles: ("owner" | "editor" | "viewer")[],
  ): Promise<boolean> {
    const result = await tx.run(
      AccessGrant.matching(
        zql.accessGrant.where("resourceId", "=", collectionId),
        actor,
        AccessGrant.criteria("list_collection", "root", roles),
      ).one(),
    );
    return !!result;
  }

  export type Rename = z.infer<typeof rename.schema>;
  export const rename = withZeroTransaction(
    z.object({
      id: z.uuid(),
      name: z.string().min(1).max(MAX_NAME_LENGTH),
    }),
    async ({ tx, ctx, args: { id, name } }) => {
      const collection = await tx.run(zql.listCollection.where("id", "=", id).one());
      if (!collection) {
        accessDenied("Collection not found");
      }

      if (collection.defaultForUserId !== null) {
        throw new Error("Cannot rename default collection");
      }

      const hasPermission = await hasRootGrant(tx, ctx.actor, id, ["owner", "editor"]);
      if (!hasPermission) {
        accessDenied("Cannot rename collection");
      }

      await tx.mutate.listCollection.update({
        id,
        name: name.trim(),
      });
    },
  );

  export type Delete = z.infer<typeof del.schema>;
  export const del = withZeroTransaction(
    z.object({ id: z.uuid() }),
    async ({ tx, ctx, args: { id } }) => {
      const collection = await tx.run(
        zql.listCollection.where("id", "=", id).related("grants").one(),
      );
      if (!collection) {
        accessDenied("Collection not found");
      }

      if (collection.defaultForUserId !== null) {
        throw new Error("Cannot delete default collection");
      }

      const hasPermission = await hasRootGrant(tx, ctx.actor, id, ["owner"]);
      if (!hasPermission) {
        accessDenied("Cannot delete collection");
      }

      const listCount = await tx.run(zql.list.where("collectionId", "=", id));
      if (listCount.length > 0) {
        throw new Error("Cannot delete collection. Remove all items first.");
      }

      await Promise.all(
        collection.grants.map((grant) => tx.mutate.accessGrant.delete({ id: grant.id })),
      );
      await tx.mutate.listCollection.delete({ id });
    },
  );
}
